import { useState } from "react";
import ExcelJS from "exceljs";
import {
  RFID_REPORTS_EXPORT_COLUMNS,
  SCAN_DETAIL_EXPORT_COLUMNS,
  buildReportExportData,
  getExportDateTime,
  loadAllReportScannedTags,
  mapScanDetailRowsForExport,
} from "./rfidReportsExportHelpers";
import { useRfidReportsStore } from "./store/useRfidReportsStore";

// Outer header — same as Asset Master
const OUTER_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "003366" },
};

// Inner header — same as Asset Master history header
const INNER_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "DBEAFE" },
};

const centerCell = (cell) => {
  cell.alignment = { horizontal: "center" };
};

const styleHeaderRow = (row, fill, columnCount, fontColor = "FFFFFFFF") => {
  const boldFont = { bold: true, color: { argb: fontColor } };
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.font = boldFont;
    centerCell(cell);
    if (colNumber <= columnCount) {
      cell.fill = fill;
    }
  });
};

export const ExportRfidReportsExcel = ({ rfidReportData = [] }) => {
  const filters = useRfidReportsStore((state) => state.filters);
  const rfidScannedTags = useRfidReportsStore((state) => state.rfidScannedTags);
  const [isExporting, setIsExporting] = useState(false);

  const exportToExcel = async () => {
    setIsExporting(true);

    try {
      const scannedTagsByKey = await loadAllReportScannedTags(
        rfidReportData,
        filters,
        rfidScannedTags
      );
      const exportData = buildReportExportData(rfidReportData, scannedTagsByKey);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("RFID List");
      const boldFont = { bold: true };
      const mainColumnCount = RFID_REPORTS_EXPORT_COLUMNS.length;
      const scanColumnCount = SCAN_DETAIL_EXPORT_COLUMNS.length;

      worksheet.addRow(["", "", getExportDateTime(), ""]);
      worksheet.mergeCells(1, 2, 1, mainColumnCount);
      worksheet.getCell(1, 2).value = getExportDateTime();
      worksheet.getCell(1, 2).font = boldFont;
      worksheet.getCell(1, 2).alignment = { horizontal: "center" };

      worksheet.addRow(["", "", "RFID List", ""]);
      worksheet.mergeCells(2, 2, 2, mainColumnCount);
      worksheet.getCell(2, 2).value = "RFID List";
      worksheet.getCell(2, 2).font = boldFont;
      worksheet.getCell(2, 2).alignment = { horizontal: "center" };

      const mainHeaderRow = worksheet.addRow(RFID_REPORTS_EXPORT_COLUMNS);
      styleHeaderRow(mainHeaderRow, OUTER_HEADER_FILL, mainColumnCount);

      exportData.forEach(({ mainRow, scanDetails, rfidCode }) => {
        worksheet.addRow(mainRow).eachCell({ includeEmpty: true }, centerCell);

        if (!scanDetails.length) return;

        const titleRow = worksheet.addRow(["", `Scan Details - ${rfidCode}`]);
        worksheet.mergeCells(
          titleRow.number,
          2,
          titleRow.number,
          1 + scanColumnCount
        );
        const titleCell = titleRow.getCell(2);
        titleCell.font = { bold: true, color: { argb: "FF003366" } };
        titleCell.fill = INNER_HEADER_FILL;

        const scanHeaderRow = worksheet.addRow([
          "",
          ...SCAN_DETAIL_EXPORT_COLUMNS,
        ]);
        // Inner header: light blue + navy text (same as Asset Master PDF / UI)
        styleHeaderRow(
          scanHeaderRow,
          INNER_HEADER_FILL,
          scanColumnCount + 1,
          "FF003366"
        );

        mapScanDetailRowsForExport(scanDetails).forEach((scanRow) => {
          worksheet
            .addRow(["", ...scanRow])
            .eachCell({ includeEmpty: true }, centerCell);
        });

        worksheet.addRow([]);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "RfidList.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating Excel file:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={exportToExcel}
      className="button-container ms-3"
      disabled={isExporting}
    >
      <i className="fa-solid fa-file-excel me-1 icon-container"></i>
      {isExporting ? "Exporting..." : "Excel"}
    </button>
  );
};
